import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

type NonEmptyString<T extends string> = '' extends T ? never : T;

/**
 * Simplified hook for using a single search param as a state value
 *  * Will delete the param if the value is an empty string
 *  * Will not update the param if the value is the same as the current value
 *
 * NOTE: This assumes the key is unique. If the key occurs multiple times in the Params, they will all be replaced by a single value
 *
 * @param key - The key of the search param, for example: path?thisKey=...
 * @param defaultValue - The default value of the search param
 * @param replaceHistory - If true, navigation history intact. Otherwise, a new navigation entry will be appended
 * @returns
 */
export const useSearchParam = <T extends string>(
  key: NonEmptyString<T>,
  defaultValue: string = '',
  replaceHistory = true,
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = searchParams.get(key) ?? defaultValue;
  const setValue = useCallback(
    (newValue: string) => {
      setSearchParams(
        (params) => {
          if (newValue === '') params.delete(key);
          else if (newValue !== params.get(key)) params.set(key, newValue);
          return params;
        },
        { replace: replaceHistory },
      );
    },
    [key, setSearchParams, replaceHistory],
  );

  return [value, setValue] as const;
};
